import React, { useState, useEffect, useContext, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AdminCompanyContext } from '../../AdminApp';
import { getAdminConversations, getAdminConversation, deleteAdminConversation } from '../../adminApi';
import { useCompanyNames, resolveCompanyName } from '../../hooks/useCompanyNames';
import { useUserNames, resolveUserName } from '../../hooks/useUserNames';

const PAGE_SIZE = 25;

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function shortId(id) {
  if (!id) return '—';
  return id.length > 14 ? id.slice(0, 6) + '…' + id.slice(-4) : id;
}

// ─── Thread viewer ────────────────────────────────────────────────────────────

function ToolDetails({ tools }) {
  const [open, setOpen] = useState(false);
  if (!tools || tools.length === 0) return null;
  return (
    <div className="admin-conv-tools">
      <button className="admin-conv-tools-toggle" onClick={() => setOpen(o => !o)}>
        {open ? '▾' : '▸'} {tools.length} tool call{tools.length !== 1 ? 's' : ''}
      </button>
      {open && (
        <div className="admin-conv-tools-list">
          {tools.map((t, i) => (
            <div key={i} className="admin-conv-tool-item">
              <div className="admin-conv-tool-name">
                {t.name}
                <span className={`admin-conv-tool-status ${t.success ? 'ok' : 'err'}`}>
                  {t.success ? '✓' : '✗'}
                </span>
                {t.execution_time_ms && <span className="admin-conv-tool-time">{t.execution_time_ms}ms</span>}
              </div>
              {t.result_preview && (
                <div className="admin-conv-tool-preview">{t.result_preview}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessageBubble({ msg }) {
  const isHuman = msg.role === 'human';
  return (
    <div className={`admin-conv-msg ${isHuman ? 'admin-conv-msg-human' : 'admin-conv-msg-ai'}`}>
      <div className="admin-conv-msg-role">{isHuman ? 'User' : 'AI'}</div>
      <div className="admin-conv-msg-body">
        {isHuman ? (
          <span>{msg.content}</span>
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content || ''}</ReactMarkdown>
        )}
      </div>
      {!isHuman && msg.metadata?.tool_details && (
        <ToolDetails tools={msg.metadata.tool_details} />
      )}
      <div className="admin-conv-msg-time">{fmtDate(msg.timestamp)}</div>
    </div>
  );
}

function ConversationThread({ sessionId, onClose }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getAdminConversation(sessionId)
      .then(data => { if (!cancelled) { setThread(data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <tr className="admin-conv-thread-row">
      <td colSpan={6}>
        <div className="admin-conv-thread">
          <div className="admin-conv-thread-header">
            <span>{thread?.title || sessionId}</span>
            <button onClick={onClose} className="admin-conv-thread-close">✕ Close</button>
          </div>
          {loading && <div className="admin-conv-thread-loading">Loading thread…</div>}
          {error   && <div className="admin-conv-thread-error">{error}</div>}
          {thread  && (
            <div className="admin-conv-thread-messages">
              {thread.messages.map(m => <MessageBubble key={m.message_id} msg={m} />)}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminConversations() {
  const { selectedCompany } = useContext(AdminCompanyContext);
  const companyNames = useCompanyNames();
  const userNames = useUserNames();

  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [company, setCompany]   = useState('');
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (selectedCompany) setCompany(selectedCompany);
  }, [selectedCompany]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminConversations({ company_id: company || undefined, limit: 200 });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(0);
      setExpanded(null);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [company]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(e, sessionId) {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation permanently?')) return;
    setDeleting(sessionId);
    try {
      await deleteAdminConversation(sessionId);
      setItems(prev => prev.filter(r => r.session_id !== sessionId));
      if (expanded === sessionId) setExpanded(null);
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setDeleting(null);
    }
  }

  // Client-side title search
  const filtered  = items.filter(r =>
    !search || (r.title || '').toLowerCase().includes(search.toLowerCase())
  );
  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div>
      {/* ── Header + filters ── */}
      <div className="admin-toolbar">
        <h2 className="admin-section-title" style={{ margin: 0 }}>Conversations</h2>
        <div className="admin-toolbar-controls">
          <input
            className="admin-company-input"
            placeholder="Filter by company ID…"
            value={company}
            onChange={e => { setCompany(e.target.value); setPage(0); }}
          />
          <input
            className="admin-company-input"
            placeholder="Search by title…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
        </div>
      </div>

      {error && (
        <div className="admin-error-state">
          <p>{error}</p>
          <button onClick={() => { setLoading(true); load(); }}>Retry</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="admin-table-meta">
            {filtered.length} conversation{filtered.length !== 1 ? 's' : ''}
            {total > items.length ? ` (showing first ${items.length} of ${total})` : ''}
            {' '}— click a row to expand the thread
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Company</th>
                  <th>User</th>
                  <th>Created</th>
                  <th>Last Updated</th>
                  <th style={{ textAlign: 'right' }}>Messages</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--admin-text-muted)' }}>No conversations found.</td></tr>
                ) : pageItems.map(row => (
                  <React.Fragment key={row.session_id}>
                    <tr
                      onClick={() => setExpanded(expanded === row.session_id ? null : row.session_id)}
                      className={expanded === row.session_id ? 'admin-row-selected' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.title || <em style={{ color: 'var(--admin-text-muted)' }}>Untitled</em>}
                      </td>
                      <td title={row.company_id}>{resolveCompanyName(companyNames, row.company_id)}</td>
                      <td title={row.user_id}>{resolveUserName(userNames, row.user_id)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.last_updated)}</td>
                      <td style={{ textAlign: 'right' }}>{row.message_count}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <button
                          className="admin-conv-delete-btn"
                          onClick={e => handleDelete(e, row.session_id)}
                          disabled={deleting === row.session_id}
                          title="Delete conversation"
                        >
                          {deleting === row.session_id ? '…' : '🗑'}
                        </button>
                      </td>
                    </tr>
                    {expanded === row.session_id && (
                      <ConversationThread
                        sessionId={row.session_id}
                        onClose={() => setExpanded(null)}
                      />
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="admin-pagination">
              <button onClick={() => setPage(p => p - 1)} disabled={page === 0}>← Prev</button>
              <span>Page {page + 1} of {pageCount}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= pageCount - 1}>Next →</button>
            </div>
          )}
        </>
      )}

      {loading && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Title</th><th>Company</th><th>User</th><th>Created</th><th>Last Updated</th><th>Messages</th><th></th></tr></thead>
            <tbody>
              {[1,2,3,4,5].map(i => (
                <tr key={i}>{[1,2,3,4,5,6,7].map(j => (
                  <td key={j}><div className="admin-skeleton" style={{ height: 14, width: '70%' }} /></td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
